import React, { useContext, useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Button, Box
} from '@mui/material';
import dayjs from 'dayjs';
import { DateRangeContext } from '../../../Context';
import axios from 'axios';
import { get_Call_Catogery } from '../../../constants';

const CategoryCallCountTable = () => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December', 'Grand Total'
  ];

  const { filterType, start, end } = useContext(DateRangeContext);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [selectedFilter, setSelectedFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);

  // 🔥 UPDATED: Transform API response to component format
  const transformApiData = (apiResponse) => {
    // 🔥 Define standard categories that should always be shown
    const standardCategories = ['closed', 'cold', 'hot', 'lost', 'pending', 'warm'];
    
    // Set categories for reference
    setCategories(standardCategories);
    
    // Always create data structure for all standard categories
    const transformedData = standardCategories.map(categoryName => {
      const categoryData = {
        leadSource: categoryName.charAt(0).toUpperCase() + categoryName.slice(1), // Capitalize first letter
        date: dayjs().format('YYYY-MM-DD'), // Use current date for filtering
        'Grand Total': 0
      };

      // Initialize all months with 0
      months.slice(0, -1).forEach(month => {
        categoryData[month] = 0;
      });

      // 🔥 UPDATED: Only fill data if API response is successful and has data
      if (apiResponse?.success && apiResponse?.data?.monthlyData) {
        const { monthlyData } = apiResponse.data;
        
        // Fill data from API response
        monthlyData.forEach(monthData => {
          const monthName = monthData.month;
          if (categoryData.hasOwnProperty(monthName)) {
            const callCount = monthData[categoryName] || 0;
            categoryData[monthName] = callCount;
            categoryData['Grand Total'] += callCount;
          }
        });
      }

      return categoryData;
    });

    // 🔥 UPDATED: Return all categories, even those with 0 data
    return transformedData; // Don't filter out categories with 0 totals
  };

  // 🔥 Fetch data from API with filter parameters
  const fetchDataFromAPI = async (currentFilterType = null, customStart = null, customEnd = null) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use current filter type or fall back to context filter type
      const activeFilterType = currentFilterType || filterType;
      const activeStart = customStart || start;
      const activeEnd = customEnd || end;

      // 🔥 Build query parameters based on filter type
      const params = {};
      
      switch (activeFilterType) {
        case 'today':
          params.filterType = 'today';
          break;
        case 'yesterday':
          params.filterType = 'yesterday';
          break;
        case 'last7':
          params.filterType = 'last7';
          break;
        case 'month':
          params.filterType = 'month';
          break;
        case 'custom':
          if (activeStart && activeEnd) {
            params.filterType = 'custom';
            params.customStartDate = dayjs(activeStart).format('YYYY-MM-DD');
            params.customEndDate = dayjs(activeEnd).format('YYYY-MM-DD');
          } else {
            params.filterType = 'none';
          }
          break;
        case 'none':
        default:
          params.filterType = 'none';
          break;
      }

      console.log('Fetching category data with params:', params);

      // 🔥 API call to your category-wise backend endpoint
      const response = await axios.get(`${get_Call_Catogery}`, {
        params: params,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Category API Response:', response.data);

      // Transform and set data
      const transformed = transformApiData(response.data);
      setData(transformed);
      setFilteredData(transformed);
      
    } catch (error) {
      console.error('Error fetching category data from API:', error);
      
      if (error.response) {
        setError(`Server Error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        setError('Network Error: Cannot reach server. Make sure backend is running.');
      } else {
        setError(`Error: ${error.message}`);
      }
      
      // 🔥 UPDATED: Even on error, show categories with 0 data
      const transformed = transformApiData(null); // Pass null to show all categories with 0
      setData(transformed);
      setFilteredData(transformed);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 Initial data fetch
  useEffect(() => {
    fetchDataFromAPI();
  }, []);

  // 🔥 Filter effect - triggers API call when filter changes
  useEffect(() => {
    fetchDataFromAPI(filterType, start, end);
  }, [filterType, start, end]);

  // Calculate grand totals for filtered data
  const grandTotals = filteredData.reduce((acc, row) => {
    months.forEach(month => {
      acc[month] = (acc[month] || 0) + (row[month] ?? 0);
    });
    return acc;
  }, {});

  // Enhanced Excel download function with full table data
  const downloadExcel = () => {
    const timestamp = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const filterInfo = getFilterDescription();
    
    const header = ['Call Category', ...months];
    
    const dataRows = filteredData.map(row => {
      const rowData = [row.leadSource];
      months.forEach(month => {
        rowData.push(row[month] ?? 0);
      });
      return rowData;
    });

    const grandTotalRow = ['Grand Total'];
    months.forEach(month => {
      grandTotalRow.push(grandTotals[month] ?? 0);
    });

    const allRows = [
      ['Category wise Call Count (Inbound/Outbound)'],
      [`Generated on: ${timestamp}`],
      [`Filter Applied: ${filterInfo}`],
      [`Total Categories: ${filteredData.length}`],
      [],
      header,
      ...dataRows,
      [],
      grandTotalRow,
      [],
      ['Category Statistics:'],
      [`Hot Categories: ${filteredData.filter(r => r.leadSource.toLowerCase().includes('hot')).length}`],
      [`Cold Categories: ${filteredData.filter(r => r.leadSource.toLowerCase().includes('cold')).length}`],
      [`Closed Categories: ${filteredData.filter(r => r.leadSource.toLowerCase().includes('closed')).length}`],
      [`Total Categories: ${filteredData.length}`]
    ];

    let csvContent = 'data:text/csv;charset=utf-8,\uFEFF';
    csvContent += allRows.map(row => 
      Array.isArray(row) 
        ? row.map(cell => `"${cell}"`).join(',') 
        : `"${row}"`
    ).join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `category_call_count_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to describe current filter
  const getFilterDescription = () => {
    if (filterType === 'today') return 'Today';
    if (filterType === 'yesterday') return 'Yesterday';
    if (filterType === 'last7') return 'Last 7 Days';
    if (filterType === 'month') return 'Current Month';
    if (filterType === 'custom') return `Custom Range: ${start} to ${end}`;
    return 'All Data (No Filter)';
  };

  // 🔥 Refresh function
  const refreshData = () => {
    fetchDataFromAPI(filterType, start, end);
  };

  // 🔥 Get category icon
  const getCategoryIcon = (category) => {
    const cat = category.toLowerCase();
    if (cat.includes('hot')) return '';
    if (cat.includes('warm')) return '';
    if (cat.includes('cold')) return '';
    if (cat.includes('closed')) return '';
    if (cat.includes('lost')) return '';
    if (cat.includes('pending')) return '';
    return '';
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          mt: 3,
          boxShadow: 2
        }}
      >
        <Box
          sx={{
            backgroundColor: '#097969',
            color: 'white',
            py: 2,
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              textAlign: 'left'
            }}
          >
             Category wise Call Count (Inbound/Outbound)
            {loading && ' (Loading...)'}
            {error && ' (Error loading data)'}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* 🔥 Refresh Button */}
            <Button
              variant="outlined"
              onClick={refreshData}
              disabled={loading}
              sx={{
                color: 'white',
                borderColor: 'white',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white'
                }
              }}
            >
              🔄 Refresh
            </Button>

            <Button
              variant="contained"
              color="success"
              onClick={downloadExcel}
              disabled={filteredData.length === 0 || loading}
              sx={{
                backgroundColor: '#097969',
                fontWeight: 'bold',
                px: 3,
                py: 1,
                '&:hover': {
                  backgroundColor: '#45a049',
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  backgroundColor: '#cccccc',
                  color: '#666'
                },
                transition: 'all 0.2s ease'
              }}
            >
              📥 Export 
            </Button>
          </Box>
        </Box>

        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>
                Call Category
              </TableCell>
              {months.map(month => (
                <TableCell key={month} align="center" sx={{ fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>
                  {month === 'Grand Total' ? month : month.slice(0, 3)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={months.length + 1}
                  align="center"
                  sx={{
                    py: 3,
                    color: '#666'
                  }}
                >
                  Loading category data...
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={months.length + 1}
                  align="center"
                  sx={{
                    py: 3,
                    color: '#666'
                  }}
                >
                  No categories available
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filteredData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: '#fafafa'
                      },
                      '&:hover': {
                        backgroundColor: '#e3f2fd'
                      }
                    }}
                  >
                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                      {getCategoryIcon(row.leadSource)} {row.leadSource}
                    </TableCell>
                    {months.map(month => (
                      <TableCell key={month} align="center" sx={{ borderBottom: '1px solid #eee' }}>
                        {/*  UPDATED: Always show 0 instead of — */}
                        <Box component="span" sx={{ 
                          display: 'inline-block',
                          minWidth: '20px' 
                        }}>
                          {row[month] ?? 0}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {/* Grand Total Row */}
                <TableRow
                  sx={{
                    backgroundColor: '#e3f2fd',
                    fontWeight: 'bold'
                  }}
                >
                  <TableCell sx={{ borderTop: '2px solid #ddd' }}>
                    Grand Total
                  </TableCell>
                  {months.map(month => (
                    <TableCell key={month} align="center" sx={{ borderTop: '2px solid #ddd' }}>
                      <Box component="span" sx={{ 
                        display: 'inline-block',
                        minWidth: '25px',
                        fontWeight: 'bold'
                      }}>
                        {grandTotals[month] ?? 0}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default CategoryCallCountTable;
